import { Box } from "@mui/material";
import * as React from "react";
import * as eventService from "../../models/services/eventService";
import * as recordService from "../../models/services/recordService";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { CompanyActiveState, ConfirmationDialogResult, DatabaseRecordType, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../framework/appUtils";
import { SaveDataObj } from "../../framework/saveData";
import { ICompanyListItem } from "../../models/company/companyListItem";
import { SelectCompanyDialog } from "../company/SelectCompanyDialog/SelectCompanyDialog";
import { ActionContainer } from "../framework/ActionContainer";
import { DialogSettingsCard } from "../framework/Container/DialogSettingsCard";
import { IListUsingComponentState, List } from "../framework/List/List";
import { ListIconButtonsItem } from "../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "../framework/List/ListLinkValue";
import { ListSearchFilter } from "../framework/List/ListSearchFilter";
import { IModulePartnerListItems } from "source/models/modulePartner/modulePartnerListItems";
import { IModulePartnerListItem } from "source/models/modulePartner/modulePartnerListItem";
import { ModulePartnerEditDialog } from "./ModulePartnerEditDialog";

interface ModulePartnersDialogCompanyListProp {
    databaseRecordType: DatabaseRecordType;
    canAddPartner: boolean;
    canRemovePartner: boolean;
    companyId: string;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    eventPartnerListItems: IModulePartnerListItems;
    onGetPartnerListItems: (page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, refreshList: boolean) => Promise<IModulePartnerListItems>;
}

interface IModulePartnersDialogCompanyListState extends IListUsingComponentState<IModulePartnerListItem> {
    // List
    isLoading: boolean;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    defaultUnitId: string;
    checkedIds: string[];

    // Dialog
    showSelectCompanyDialog: boolean;
}

export class ModulePartnersDialogCompanyList extends React.Component<ModulePartnersDialogCompanyListProp, IModulePartnersDialogCompanyListState> {
    getStateFromEventPartnerListItems = (state: IModulePartnersDialogCompanyListState, eventPartnerListItems: IModulePartnerListItems,
        page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, eventPartnerListItems.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        return {
            page: !refreshList ? eventPartnerListItems.page : page,
            hasMore: eventPartnerListItems.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            defaultUnitId: eventPartnerListItems.defaultUnitId,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || eventPartnerListItems.hasMore) : state.editDialogHasNext,
        };
    };
    
    constructor(props: ModulePartnersDialogCompanyListProp) {
        super(props);
        const state = {
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: props.filter,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            defaultUnitId: "",
            items: [],
            selectedId: null,
            checkedIds: [],
            showSelectCompanyDialog: false,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            detailsItem: null
        };
        const newState = this.getStateFromEventPartnerListItems(state, props.eventPartnerListItems, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
        this.state = {
            ...state,
            ...newState
        };
    }

    searchItems = async (page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await this.props.onGetPartnerListItems(page, filter, sortColumn, sortOrderIsAsc, refreshList);
        if (!result) return;
        this.setState({ isLoading: false });
        this.setState(this.getStateFromEventPartnerListItems(this.state, result, page, filter, sortColumn, sortOrderIsAsc, resetItems, refreshList, moveNext, setEditDialogData));
    };

    refreshList = async (setEducationDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    componentDidUpdate(prevProps: ModulePartnersDialogCompanyListProp, prevState: IModulePartnersDialogCompanyListState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems(state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(!nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    // #region Edit Dialog
    getSaveData = (eventPartnerId: string, partnerCompanyId: string, unitId: string, rowId: string): SaveDataObj => {
        const props = this.props;
        const result = new SaveDataObj();
        result.append("id", eventPartnerId);
        result.append("companyId", props.companyId);
        result.append("partnerCompanyId", partnerCompanyId);
        result.append("unitId", unitId);
        result.append("rowId", rowId);
        return result;
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    handleEditDialogOk = () => {
        this.refreshList(false);
    };

    handleEditDialogCancel = (refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshList) return;
        this.refreshList(false);
    };
    // #endregion Edit Dialog

    // #region Select Company
    handleSelectCompanyDialogOk = async (companies: ICompanyListItem[]) => {
        const state = this.state;
        if (!companies || companies.length < 1) return;
        const saveData = this.getSaveData(Base.emptyGuid, companies[0].id, state.defaultUnitId, "");
        const result = this.props.databaseRecordType === DatabaseRecordType.Event
            ? await AppUtils.callService(() => eventService.saveEventPartnerEdit(saveData.obj))
            : await AppUtils.callService(() => recordService.saveRecordUserPartnerEdit(saveData.obj));
        if (!result) return;
        this.setState({
            showSelectCompanyDialog: false,
        });
        await this.refreshList(false);
        this.setState(List.getShowEditDialogStateChange(this.state, result.id));
    };

    onSelectCompanyDialogCancel = () => {
        this.setState({
            showSelectCompanyDialog: false
        });
    };
    // #endregion Select Company

    remove = async (id: string, code: string): Promise<ApiSuccess> => {
        const props = this.props;
        if (!id) return;
        // Call server
        const result = props.databaseRecordType === DatabaseRecordType.Event
            ? await AppUtils.callService(() => eventService.removeEventPartner(props.companyId, id, code))
            : await AppUtils.callService(() => recordService.removeRecordUserPartner(props.companyId, id, code));
        if (!result) return null;
        this.refreshList();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const partner = state.items.find(i => i.id === selectedIds[0]);
        if (!partner) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemovePartnerParameter, partner.name), false) === ConfirmationDialogResult.Yes) {
            // Call server
            this.remove(partner.id, partner.code);
        }
    };
    
    handleAdd = () => {
        this.setState({
            showSelectCompanyDialog: true
        });
    };
    
    handleOpen = (id: string) => {
        const state = this.state;
        if (!id) return;
        const partner = state.items.find(i => i.id === id);
        if (!partner) return;
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <DialogSettingsCard
                fitToContainerHeight
                title={props.databaseRecordType === DatabaseRecordType.Event ? Translations.EventPartners : Translations.RecordUserPartners}
                cardContent={
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter}
                            />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddPartner}
                                addDisabled={!props.canAddPartner}
                                removeTooltip={Translations.RemovePartner}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={props.canAddPartner ? this.handleAdd : null}
                                onRemove={props.canRemovePartner ? this.handleRemove : null}
                            />
                        }
                    />
                }
                content={
                    <>
                        <List
                            height="100%"
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            header={
                                {
                                    columns: [
                                        { size: 6, column: "name", content: Translations.Name },
                                        { size: 6, column: "unit", content: Translations.Unit },
                                    ]
                                }
                            }
                            lines={
                                state.items.map((item) => {
                                    return {
                                        columns: [
                                            {
                                                size: 6,
                                                content:
                                                    <Box display="flex" alignItems="center">
                                                        <AvatarImage
                                                            name={item.name}
                                                            image={item.logo}
                                                            tooltip={item.name}
                                                            marginRight=".5rem"
                                                        />
                                                        <ListLinkValue onClick={() => this.handleOpen(item.id)}>{item.name}</ListLinkValue>
                                                    </Box>
                                            },
                                            { size: 6, content: item.unit }
                                        ],
                                        id: item.id,
                                        classes: EnumHelper.isEqual(item.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                    };
                                })}
                            noDataMessage={Translations.AddPartners}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            globalLoadingInUse={true}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpen}
                        />
                        {state.showSelectCompanyDialog &&
                            <SelectCompanyDialog
                                multiSelect={true}
                                ignoreCompanyIds={state.items.map(i => i.partnerCompanyId).concat([props.companyId])}
                                onOk={this.handleSelectCompanyDialogOk}
                                onCancel={this.onSelectCompanyDialogCancel}
                                onGetCompanyListItems={props.databaseRecordType === DatabaseRecordType.Event
                                    ? eventService.getSelectPartnerCompaniesListItems
                                    : recordService.getSelectPartnerCompaniesListItems}
                            />
                        }
                        {state.showEditDialog &&
                            <ModulePartnerEditDialog
                                databaseRecordType={props.databaseRecordType}
                                canRemovePartner={props.canRemovePartner}
                                id={state.editDialogId}
                                companyId={props.companyId}
                                hasPrevious={state.editDialogHasPrevious}
                                hasNext={state.editDialogHasNext}
                                onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                                onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                                onSaved={this.handleEditDialogOk}
                                onClose={this.handleEditDialogCancel}
                                onRemove={this.remove}
                                onRefreshList={() => this.refreshList(false)}
                            />
                        }
                    </>
                }
            />
        );
    }
}